"use client";

import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Sparkles, Brain, Globe, Users, Palette, Zap } from "lucide-react";

export interface TalktribeFeaturesBlockProps {
    tag?: string;
    title?: string;
    features?: Array<{
        iconName?: string;
        title: string;
        desc: string;
    }>;
}

const iconMap: Record<string, React.ComponentType<any>> = {
    MessageCircle,
    Sparkles,
    Brain,
    Globe,
    Users,
    Palette,
    Zap
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export default function TalktribeFeaturesBlock({
    tag = "Key Features",
    title = "What Powers TalkTribe",
    features = [
        { iconName: "MessageCircle", title: "Instant Character Chat", desc: "Pick any character from the gallery and start chatting immediately. No signup required to explore." },
        { iconName: "Sparkles", title: "Create Characters by Prompt", desc: "Describe a character in natural language and the system generates a full persona — personality, voice, and backstory." },
        { iconName: "Brain", title: "Persistent Memory", desc: "Characters remember past conversations. They recall context, build relationships, and evolve over time." },
        { iconName: "Globe", title: "Multi-Language Support", desc: "Chat in 40+ languages. Characters adapt their responses to match the user's preferred language natively." },
        { iconName: "Users", title: "Character Discovery", desc: "Browse characters by category — anime, fantasy, romance, history, movie & TV — with trending and curated collections." },
        { iconName: "Palette", title: "Custom Personas", desc: "Full persona customization including personality traits, conversation style, and behavioral rules." }
    ]
}: TalktribeFeaturesBlockProps) {
    return (
        <section id="features" className="py-24 px-6 md:px-12 lg:px-24 bg-[#060608] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-violet-400 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">{title}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, i) => {
                        const IconComponent = iconMap[feature.iconName || ""] || MessageCircle;
                        return (
                            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                                className="bg-[#0d0d10] border border-zinc-900 rounded-xl p-7 space-y-4 hover:border-zinc-800 transition-colors group">
                                <div className="p-2.5 bg-violet-500/10 border border-violet-500/20 rounded-lg text-violet-400 w-fit group-hover:bg-violet-500/15 transition-colors">
                                    <IconComponent className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-medium text-white">{feature.title}</h3>
                                <p className="text-zinc-500 text-sm leading-relaxed font-light">{feature.desc}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
